import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigation } from '@remix-run/react';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import type { AuthProvider, AuthSource, AuthType } from '../../user/type';
import LoginSignUpModal from '../../user/component/login-signup-modal/LoginSignUpModal';
import { useUser } from '@modules/user/hook/use-user';
import type { LoginFlow } from '~/modules/user/hook/use-login-flow';
import { isCleanRedirect } from '~/modules/root';
import { trackAuthEvent } from '../../user/util/track-auth-event';
import { useTracker } from '@archipro-website/tracker';
import { AUTH_EVENTS } from '~/modules/root/hook/use-auth-event';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { handleAuthRedirect } from '~/modules/root/util/handle-auth-redirect';
import { ENQUIRE_MODAL_SHOWUP_KEY } from '~/modules/enquiry/constants';

interface AuthState {
    open: boolean;
    authSource: AuthSource;
    flow?: LoginFlow;
    redirect?: string;
    allowExternalUrls?: boolean;
    skipPostSignUp?: boolean;
    disableCloseButton?: boolean;
    subtitle?: string;
    defaultPublicUser?: PublicUser;
    onCancel?: () => void;
    onConfirm?: () => void;
    onSuccess?: () => void;
    onAuthSuccess?: () => void;
}

interface AuthRedirectState extends Omit<AuthState, 'open'> {
    provider: AuthProvider;
    authType: AuthType;
}

export const AuthContext = React.createContext<
    | {
          showAuthModal: (props: Omit<AuthState, 'open'>) => void;
          hideAuthModal: () => void;
          authRedirect: (props: AuthRedirectState) => void;
      }
    | undefined
>(undefined);

export const AuthContextProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [state, setState] = useState<AuthState>({
        authSource: 'topNav',
        open: false,
    });

    const [redirectState, setRedirectState] =
        useState<AuthRedirectState | null>(null);

    const location = useLocation();
    const user = useUser();
    const navigation = useNavigation();
    const tracker = useTracker();
    const { isFirefox } = useDeviceBrowserContext();

    const showModal = useCallback((props: Omit<AuthState, 'open'>) => {
        setState({ ...props, open: true });
    }, []);

    const hideModal = useCallback(() => {
        setState({ authSource: 'topNav', open: false });
    }, []);

    const redirect = useCallback((props: AuthRedirectState) => {
        setRedirectState(props);
    }, []);

    useEffect(() => {
        if (
            !redirectState ||
            user.__typename !== 'Me' ||
            navigation.state == 'loading'
        )
            return;
        const guestID = user.TrackedGuest?.ID ?? 0;
        const { pathname, search } = location;
        const {
            redirect,
            allowExternalUrls,
            authSource,
            provider,
            authType,
            skipPostSignUp,
        } = redirectState;

        let newURL =
            redirect && isCleanRedirect(redirect, allowExternalUrls)
                ? decodeURI(redirect)
                : `${pathname}${search}`;

        if (pathname === '/login' && newURL === '/login') {
            newURL = '/';
        }
        const originSearchParams = new URLSearchParams(search);
        const encodedRedirect = originSearchParams.get('encodedRedirect');
        if (encodedRedirect) {
            newURL = `/linkshare?encoded=${encodedRedirect}`;
        } else {
            if (!skipPostSignUp) {
                const searchParams = new URLSearchParams();
                if (newURL !== '/') {
                    let redirectURL = newURL;
                    if (redirectURL.includes(ENQUIRE_MODAL_SHOWUP_KEY)) {
                        const url = new URL(newURL, window.location.origin);
                        url.searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);
                        redirectURL = url.toString();
                    }
                    searchParams.set('redirect', encodeURI(redirectURL));
                }
                const search = searchParams.toString();
                newURL = `/post-sign-up${search ? `?${search}` : ''}`;
            }
        }

        const redirectURL = async () => {
            await trackAuthEvent({
                tracker,
                event: AUTH_EVENTS[authType],
                provider,
                authSource,
                guestID,
            });

            await handleAuthRedirect({ newURL, authSource, isFirefox });
        };

        setRedirectState(null);
        redirectURL();
    }, [
        location,
        setState,
        tracker,
        user,
        navigation.state,
        redirectState,
        isFirefox,
    ]);

    return (
        <AuthContext.Provider
            value={{
                showAuthModal: showModal,
                hideAuthModal: hideModal,
                authRedirect: redirect,
            }}
        >
            {children}
            {/* Need to re-render modal to apply defaultPublicUser changes. */}
            {/* TODO: Fix this so that we can always render the modal. */}
            {state.open && (
                <LoginSignUpModal
                    login={{
                        onOpen: () => showModal({ ...state }),
                        onCancel: hideModal,
                        onConfirm: hideModal,
                        onSuccess: hideModal,
                        onAuthSuccess: hideModal,
                        flow: null,
                        ...state,
                    }}
                    forgotPassword={{
                        open: state.open,
                        onCancel: hideModal,
                    }}
                />
            )}
        </AuthContext.Provider>
    );
};

/**
 * Hook to provide functions to show and hide the login/signup modal. Must
 * be used within the `AuthContext.Provider`.
 */
export function useAuth() {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error('Cannot be used outside AuthContext');
    }
    return context;
}
